<script setup lang="ts">
import {Trip} from '@/models/Trip';
import {computed, onMounted, onUpdated, PropType, ref, watch} from "vue";
import CustomButton from "@/components/Button/Button.vue";
import {SavedOffer} from "@/models/SavedOffer";
import {CrewMember} from '@/models/CrewMember';
import {getSavedOffers, getTripCrew} from "@/services/trip.service";
import TimePicker from "@/components/TimePicker/TimePicker.vue";
import {useI18n} from 'vue-i18n';
import Affiliates from "./components/Affiliaties.vue";
import {useUserStore} from "@/stores/userStore";

const props = defineProps({
  trip: {
    type: Object as PropType<Trip>,
    required: true
  },
});

const {t} = useI18n();
const userStore = useUserStore();

defineEmits([
  'go-to-offers',
  'go-to-crew',
  'go-to-itinerary',
  'go-to-budget'
]);

const routes = [
  {
    title: 'Aegean Thesaurus',
    duration: '7 days',
    img: 'https://www.visitgreece.gr/images/1743x752/jpg/files/i_1223762770_santorini-oia_1743x752.jpg',
  },
  {
    title: 'Aegean Thesaurus',
    duration: '7 days',
    img: 'https://www.visitgreece.gr/images/1743x752/jpg/files/i_1223762770_santorini-oia_1743x752.jpg',
  },
  {
    title: 'Aegean Thesaurus',
    duration: '7 days',
    img: 'https://www.visitgreece.gr/images/1743x752/jpg/files/i_1223762770_santorini-oia_1743x752.jpg',
  },
  {
    title: 'Cycladic overview',
    duration: '7 days',
    img: 'https://www.visitgreece.gr/images/1743x752/jpg/files/s_1217691214_crete-elafonisi_1743x752.webp'
  },
];


const tripBudget = computed(() => ({
  total: 1000,
  expenses: 300,
  remaining: 700
}));

const statusStyle = computed(() => {
  switch (props.trip.status) {
    case 'Active':
    case 'Completed':
      return {background: '#9be7b9'};
    case 'Cancelled':
      return {background: '#ff9d9d'};
    case 'Planned':
      return {background: '#f9f9f9'};
    default:
      return {background: 'black'};
  }
});
const statusText = computed(() => {
  switch (props.trip.status) {
    case 'Active':
      return t('tripStatus.confirmed');
    case 'Completed':
      return t('tripStatus.completed');
    case 'Cancelled':
      return t('tripStatus.cancelled');
    case 'Planned':
      return t('tripStatus.planned');
    default:
      return t('tripStatus.unknown');
  }
});

const radialBarOptions = computed(() => ({
  chart: {
    id: 'budget-radial',
    label: 'budget-radial'
  },
  plotOptions: {
    radialBar: {
      hollow: {
        size: "70%",
      },
      track: {
        background: '#ffffff50',
        strokeWidth: '100%',
      },
      dataLabels: {
        showOn: "always",
        name: {
          offsetY: -43,
          show: true,
          color: "#fff",
          fontSize: "12px",
          fontWeight: 200,
        },
        value: {
          color: "#fff",
          fontSize: "30px",
          fontWeight: 550,
          formatter: () => `€ ${tripBudget.value.expenses},00`,
          show: true
        }
      }
    }
  },
  fill: {
    colors: ['#fff']
  },
  stroke: {
    lineCap: "round",
  },
  labels: [[t('budget.currentBalance')]]
}));

const radialBarSeries = computed(() => [tripBudget.value.expenses / tripBudget.value.total * 100]);
const offers = ref<SavedOffer[]>([]);
const crew = ref<CrewMember[]>([]);

onMounted(() => {

  if (props.trip && offers.value.length === 0 && crew.value.length === 0) {
    getSavedOffers(props.trip.uuid).then(data => {
      offers.value = data.data;
    });
    getTripCrew(props.trip.uuid).then(data => {
      crew.value = data.data;
    });
  }
});

const tripChecklist = ref([ // inprogress || completed || empty
  {id: 1, status: 'inprogress'},
  {id: 2, status: ''},
  {id: 3, status: ''},
  {id: 4, status: ''},
  {id: 5, status: ''},
  {id: 6, status: ''},
]);

const checkListApproved = computed(() => tripChecklist.value[0].status);
const checkListDownPay = computed(() => tripChecklist.value[1].status);
const checkListRestPay = computed(() => tripChecklist.value[2].status);
const checkListCrewInfo = computed(() => tripChecklist.value[3].status);
const checkListArrivalTime = computed(() => tripChecklist.value[4].status);
const checkListTripReady = computed(() => tripChecklist.value[5].status);

// Added this for help with styling, maybe use provide->inject for the data instead of props
const bookingSummary = computed(()=>{

  return {
    location: 'PAROIKIA PORT, PAROS, GREECE',
    locationFrom: 'London (All airports)',
    boat: 'Gloria Catamaran',
    boatyear: '2002',
    checkin:  userStore.formatDateWithMapping(props.trip.dateStart,"longText") || '',
    checkout: userStore.formatDateWithMapping(props.trip.dateEnd,"longText") || '',
    price: 6530,
    status: props.trip.status,
    manager: {
      name: 'Ioannis Papadopoulos',
      phone: '+30 6940 000 000'
    },
  }
});
const tripState = computed(()=>{
   let state = 'offer'

  switch (props.trip.status) {

    case 1:{
      state='requested'
      break;
    }

    case 2:{
      state='payment'
      break;
    }

    default: {
      state = 'offer';
    }

  }

  return state

})


onUpdated(() => {

  let el2 = document.querySelectorAll('.v-progress-item.completed');
  if (el2) {
    if (el2.length > 2) {
      el2[el2.length - 2].classList.remove('last-completed');
    }

    if (el2.length !== 0) {
      el2[el2.length - 1].classList.add('last-completed');
    }

    let el3 = document.getElementById('crew');

    let elprog = document.querySelector(".v-progress-item.inprogress");

    if (elprog) {
      let paddingY = parseFloat(getComputedStyle(elprog).paddingBottom);
      let contentHeight = elprog.offsetHeight - paddingY;

      el3.style.setProperty('--v-progress-inprogress-height', contentHeight + 8 + 'px');
    }
  }
});

watch(tripChecklist.value, (newVal) => {
  const crewSelector = document.getElementById('crew');
  let getHeight = 40;

  if (tripChecklist.value[1]['status'] == 'inprogress' || tripChecklist.value[2]['status'] == 'inprogress') {
    getHeight = 80;
  }

  crewSelector.style.setProperty("--v-progress-inprogress-height", getHeight + 'px');
  crewSelector.style.setProperty("--v-last-completed-height", "calc((100% + var(--v-progress-inprogress-height)))");

  let lastCompleted = document.querySelector('.last-completed');

  if (lastCompleted !== null) {
    lastCompleted.classList.remove('last-completed');
  }

  if (newVal[5].status) {
    const target = tripChecklist.value.find((obj) => obj.id === 6);
    Object.assign(target, {id: 6, status: 'completed'});
  }
});

</script>

<template>
  <!-- offers -->
  <div class="container overview-content flex flex-row gap-4">
    <div class="overview-content-left flex flex-col gap-4 w-2/4">
      <div v-if="tripState === 'payment'"
           class="overview-card days-count_main bg-primary-ciel p-7 rounded-11xl flex justify-between items-baseline">
        <div class="flex gap-4 items-baseline">
          <div class="days-count responsive-title-1">{{ t('tripOverview.daysCount', {days: trip.daysUntil}) }}</div>
          <div class="days-trip__text responsive-body-large">{{ t('tripOverview.forYourTrip', {destination: 'Paros'}) }}</div>
        </div>
        <div class="header-button  responsive-body-large underline font-semibold cursor-pointer">{{
            t('tripOverview.setArrivalTime')
          }}
        </div>
      </div>
      <div v-if="tripState === 'requested' || tripState === 'payment'"
           class="overview-card bg-gradient-to-r from-gradients-ghostWhite to-gradients-aliceBlue to-40% p-7 rounded-11xl">
        <ElCollapse model-value="savedOffers" id="booking">
          <ElCollapseItem name="savedOffers">
            <template #title>
              <div class="flex w-full justify-space-between">
                <div class="font-semibold">{{ t('tripOverview.bookingSummary') }}</div>
                <div class="header-button font-semibold underline cursor-pointer">{{ t('tripOverview.addToCalendar') }}
                </div>
              </div>
            </template>
            <div v-if="!bookingSummary" class="flex justify-center items-center min-h-[13.5em]">
              <div class=" responsive-body-large text-center">{{ t('tripOverview.noBooking') }}</div>
            </div>
            <div v-else class="flex flex-col gap-y-8">
              <div class="flex flex-col">
                <div class="flex items-center gap-1">
                  <img class="w-4" src="@/assets/icons/svg/Location_16x16.svg" alt="location"/>
                  <div class="overview-card__location">{{ bookingSummary.location }}</div>
                </div>
                <div class='responsive-title-6 font-bold'>{{ bookingSummary.boat }} • {{ bookingSummary.boatyear }}
                </div>
              </div>
              <div class="flex sm:justify-between gap-5">
                <div>
                  <div class="flex align-center gap-1.5 opacity-60">
                    <img class="w-4 h-4" src="@/assets/icons/svg/Calendar_16x16.svg" alt="location"/>
                    <div class="booksummary-subtitle__text text-sm">{{ t('tripOverview.checkIn') }}</div>
                  </div>
                  <div class="booksummary-content__text responsive-title-6">{{ bookingSummary.checkin }}</div>
                </div>
                <div>
                  <div class="flex align-center gap-1.5 opacity-60">
                    <img class="w-4 h-4" src="@/assets/icons/svg/Calendar_16x16.svg" alt="location"/>
                    <div class="booksummary-subtitle__text text-sm">{{ t('tripOverview.checkOut') }}</div>
                  </div>
                  <div class="booksummary-content__text responsive-title-6">{{ bookingSummary.checkout }}</div>
                </div>

                <div class="sm:flex hidden flex-col">
                  <div class="flex align-center gap-1.5 opacity-60 booksummary-subtitle__text text-sm">{{
                      t('tripOverview.baseManager')
                    }}
                  </div>
                  <div class="flex flex-col booksummary-manager-content__text">
                    <div>
                      {{ bookingSummary.manager.name }}
                    </div>
                    <div class="font-bold underline decoration-1">{{ bookingSummary.manager.phone }}</div>
                  </div>
                </div>
              </div>
              <div class="flex justify-space-between align-center">
                <div class="flex gap-4 items-center">
                  <div class="booksummary-content-price__text responsive-title-4 font-semibold">€ {{ bookingSummary.price }}</div>
                  <div class="booksummary-status__button  responsive-body-small rounded-11xl flex h-fit py-1 px-3 gap-2"
                       :style="statusStyle">
                    <img v-if="trip.status === 'Active' || trip.status === 'Planned'"
                         src="@/assets/icons/svg/Confirmed_16x16.svg" alt="" width="16" height="16"/>
                    <img v-else-if="trip.status === 'Completed'" src="@/assets/icons/svg/Completed_16x16.svg" alt=""
                         width="16" height="16"/>
                    <img v-else-if="trip.status === 'Cancelled'" src="@/assets/icons/svg/Cancelled_16x16.svg" alt=""
                         width="16" height="16"/>
                    <span style="line-height:17px">{{ statusText }}</span>
                  </div>
                </div>
                <div
                    class="arrow__btn border border-black px-5 flex items-center justify-center py-2 rounded-12xl cursor-pointer h-7">
                  <img src="@/assets/icons/svg/Arrow_right_16x16.svg" alt="arrow"/>
                </div>
              </div>
            </div>
          </ElCollapseItem>
        </ElCollapse>
      </div>

      <div v-if="tripState === 'offer'" class="overview-card bg-primary-ciel p-7 rounded-11xl">
        <ElCollapse model-value="savedOffers" id="saved-offers">
          <ElCollapseItem name="savedOffers">
            <template #title>
              <div class="flex w-full align-center justify-space-between">
                <div class="font-semibold">{{ t('tripOverview.savedOffers') }}</div>
                <div class="underline cursor-pointer">
                  <div class="header-button">{{ t('tripOverview.seeAll') }}</div>
                </div>
              </div>
            </template>
            <div class="flex flex-col align-center" v-if="!offers">
              <img src="@/assets/images/no-offer-img.svg" alt="saved-offer-icon">
              <div class="flex align-center items-center justify-center font-semibold text-center">
                {{ t('tripOverview.noSavedOffers') }}
              </div>
              <CustomButton type="black" class=" responsive-body-medium w-fit font-semibold px-6 mt-2" :rounded="true">
                {{ t('tripOverview.startExploring') }}
              </CustomButton>
            </div>
            <div class="w-full" v-else>
              <div class="offers-list" v-for="offer in offers" :key="offer.offer.uuid">
                <div class="flex justify-between" v-for="port in offer.ports" :key="port.uuid">
                  <div class="flex gap-2 align-baseline">
                    <div class="font-semibold responsive-title-5">{{ port.port }}</div>
                    <span class="text-xs"><span class="font-semibold">{{
                        port.boats.length
                      }}</span> {{ $t('savedOffers') }}</span>
                  </div>
                  <div
                      class="transition-all arrow__btn border border-black px-5 flex items-center justify-center py-2 rounded-12xl cursor-pointer h-7 hover:bg-black">
                    <img src="@/assets/icons/svg/Arrow_right_16x16.svg" alt="arrow"
                         class="transition-[filter] arrow__btn_icon"/>
                  </div>
                </div>
              </div>
            </div>
          </ElCollapseItem>
        </ElCollapse>
      </div>

      <div v-if="tripState === 'payment'" class="overview-card bg-primary-orange p-7 rounded-11xl">
        <ElCollapse model-value="savedOffers" id="arrival-time">
          <ElCollapseItem name="savedOffers">
            <template #title>
              <div class="flex justify-between align-center w-full">
                <div class="font-semibold">{{ t('tripOverview.arrivalInformation') }}</div>
                <div class="header-button font-semibold underline cursor-pointer">{{
                    t('tripOverview.editInformation')
                  }}
                </div>
              </div>
            </template>
            <div class="flex justify-between gap-8 p-2 flex-wrap">
              <div>
                <div class="arrival-time-subtitle__text flex flex-row text-sm">
                  {{ t('tripOverview.arrivalTime') }}
                </div>
                <div class="arrival-time-content flex align-baseline gap-2">
                  <TimePicker :hour="props.trip.arrivalTimeHour" :minutes="props.trip.arrivalTimeMinutes"/>
                </div>
              </div>
              <div>
                <div class="arrival-time-subtitle__text flex flex-row text-sm">
                  <span>{{ t('tripOverview.arrivalMethod') }}</span>
                </div>
                <div class="arrival-time-flight__content responsive-title-6" v-if="props.trip.arrivalMethod == 'flight'">
                  {{ t('tripOverview.flight') }}
                </div>

                <div class="arrival-time-flight__content responsive-title-6"  v-if="props.trip.arrivalMethod == 'car'">
                  {{ t('tripOverview.car') }}
                </div>

              </div>
              <div>
                <div class="arrival-time-subtitle__text flex flex-row text-lh">
                  <span>{{ t('tripOverview.flightNumber') }}</span>
                </div>
                <div class="arrival-time-flight__content responsive-title-6">
                  {{props.trip.arrivalMethodDetails}}
                </div>
              </div>
            </div>
          </ElCollapseItem>
        </ElCollapse>
      </div>
      <div v-if="tripState === 'requested' || tripState === 'payment' || (tripState === 'offer' && offers)"
           class="overview-card bg-primary-green p-7 rounded-11xl">
        <ElCollapse model-value="savedOffers" id="flight-options">
          <ElCollapseItem name="savedOffers">
            <template #title>
              <div class="flex justify-between align-center w-full">
                <div class="font-semibold">{{ t('tripOverview.flightOptions') }}</div>
                <img class="xl:w-[6.5vw] w-24" src="@/assets/images/skyscanner-logo.png" alt="skyscanner logo">
              </div>
            </template>
            <div class="flex flex-col p-2">
              <div class="flight-subtitle__text flex flex-row text-sm">
                <img src="@/assets/icons/svg/Location_16x16.svg" alt="location" height="16"/>
                <span>{{ t('tripOverview.flyingFrom') }}</span>
              </div>
              <div class="flex flex-row items-center gap-8">
                <VTextField id="flight-from__field" :readonly="true" :value="bookingSummary.locationFrom"
                            variant="underlined"/>

                <div
                    class="arrow__btn border border-black px-5 flex items-center justify-center py-2 rounded-12xl cursor-pointer h-7">
                  <img src="@/assets/icons/svg/Arrow_right_16x16.svg" alt="arrow"/>
                </div>

              </div>
            </div>
          </ElCollapseItem>
        </ElCollapse>
      </div>
      <div v-if="tripState === 'requested'"
           class="overview-card bg-gradient-to-r from-gradients-ghostWhite to-gradients-aliceBlue to-40% p-7 rounded-11xl">
        <ElCollapse model-value="savedOffers" id="suggested-itinerary">
          <ElCollapseItem name="savedOffers">
            <template #title>
              <div class="flex w-full justify-between">
                <div>{{ t('tripOverview.suggestedItinerary') }}</div>
                <div v-if="offers.length !== 0" class="header-button underline cursor-pointer"
                     @click="event => { event.stopPropagation(); $emit('go-to-itinerary') }">
                  {{ t('tripOverview.seeAll') }}
                </div>
              </div>
            </template>
            <div>
              <div class="flex flex-row overflow-x-scroll gap-8">
                <div v-for="route in routes" :key="route.title">
                  <div :style="{ backgroundImage: `url('${route.img}')` }"
                       class="routes-img bg-cover rounded-11xl flex items-start w-[10vw] h-56">
                    <div class="p-5 responsive-title-5 text-white flex items-center">
                      {{ route.title }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ElCollapseItem>
        </ElCollapse>
      </div>
      <Affiliates :display="tripState === 'payment'"/>
    </div>
    <div class="overview-content-right flex flex-col gap-4 w-2/4">
      <div v-if="tripState === 'payment' || tripState === 'requested'" id="trip-checklist"
           class="overview-card bg-primary-yellow p-7 rounded-11xl">
        <ElCollapse model-value="savedOffers" id="crew">
          <ElCollapseItem name="savedOffers">
            <template #title>
              <div class="flex w-full justify-between">
                <div>{{ t('tripOverview.tripChecklist') }}</div>
                <div class="header-button underline cursor-pointer">{{ t('tripOverview.addToCalendar') }}</div>
              </div>
            </template>

            <div class="v-progress">
              <ul>
                <li :class="checkListApproved" class="v-progress-item">
                  <div class="content request">
                    <span class="waiting">{{ t('tripOverview.pendingApproval') }}</span>
                    <span class="approved hidden">{{ t('tripOverview.requestApproved') }}</span>
                  </div>
                </li>
                <li :class="checkListDownPay" class="v-progress-item">
                  <div class="content">
                    <div class="v-progress-content flex items-baseline">
                      <div class="pay-content gap-2 flex">
                        <div>{{ t('tripOverview.downPayment') }} (50%):</div>
                        <div class="down-price">€ {{ bookingSummary.price }}</div>
                        <div class="payment-date align-center">
                          <span class="paid hidden">{{ t('tripOverview.paid') }}</span><span class="until">{{
                            t('tripOverview.until')
                          }}</span>12/12/2023
                        </div>
                        <div>
                          <button class="rest-payment hidden">
                            {{ t('tripOverview.pay') }} € {{ bookingSummary.price / 2 }}
                          </button>
                        </div>
                      </div>
                      <div>
                        <div class="receipt hidden underline cursor-pointer ml-2">
                          {{ t('tripOverview.viewReceipt') }}
                        </div>
                      </div>
                    </div>
                  </div>
                </li>

                <li :class="checkListRestPay" class="v-progress-item">
                  <div class="content">
                    <div class="v-progress-content flex items-baseline">
                      <div class="pay-content gap-2 payment">
                        {{ t('tripOverview.restPayment') }} (50%):
                        <div class="rest-price">
                          € {{ bookingSummary.price / 2 }}
                        </div>
                        <div class="payment-date align-center">
                          <span class="paid hidden">{{ t('tripOverview.paid') }}</span><span class="until">{{
                            t('tripOverview.until')
                          }}</span>31/12/2023
                        </div>
                        <div>
                          <button class="rest-payment hidden">
                            {{ t('tripOverview.pay') }} € {{ bookingSummary.price / 2 }}
                          </button>
                        </div>
                      </div>

                      <div>
                        <div class="receipt hidden underline cursor-pointer ml-2">
                          {{ t('tripOverview.viewReceipt') }}
                        </div>
                      </div>

                    </div>
                  </div>
                </li>

                <li :class="checkListCrewInfo" class="v-progress-item">
                  <div class="content">
                    <div class="flex gap-2 items-baseline">
                      <div class="pay-content gap-2 flex">
                        {{ t('tripOverview.fillInCrewInfo') }}
                        <div class="payment-date align-center">
                          <span class="paid hidden">{{ t('tripOverview.sent') }}</span><span class="until">{{
                            t('tripOverview.until')
                          }}</span>20/04/2024
                        </div>
                      </div>

                    </div>
                  </div>
                </li>

                <li :class="checkListArrivalTime" class="arrival-time v-progress-item">
                  <div class="content">
                    <div class="v-progress-content flex items-baseline">
                      <div class="pay-content gap-2 flex">
                        {{ t('tripOverview.setArrivalTime') }}
                        <div class="payment-date align-center">
                          <span class="paid hidden">{{ t('tripOverview.sent') }}</span><span class="until">{{
                            t('tripOverview.until')
                          }}</span>28/04/2023
                        </div>
                      </div>

                    </div>
                  </div>
                </li>

                <li :class="checkListTripReady" class="v-progress-item">
                  <div class="content request">
                    <div class="flex gap-2 items-baseline">
                      {{ t('tripOverview.tripReady') }}
                    </div>
                  </div>
                </li>
              </ul>
            </div>


          </ElCollapseItem>
        </ElCollapse>

      </div>
      <div class="flex gap-x-4 double-col">
        <div class="overview-card crew-card bg-gradient-to-r from-gradients-ghostWhite to-gradients-aliceBlue to-40% p-7 rounded-11xl w-2/4">
          <ElCollapse model-value="savedOffers" id="crew">
            <ElCollapseItem name="savedOffers">
              <template #title>
                <div class="flex w-full justify-between">
                  <div>{{ t('tripOverview.crewMembers') }}</div>
                  <div class="underline cursor-pointer"
                       @click="event => { event.stopPropagation(); $emit('go-to-crew') }">
                    <div class="header-button">{{ t('tripOverview.viewDetails') }}</div>
                  </div>
                </div>
              </template>
              <div class="flex flex-col gap-2">
                <div v-for="member in crew" :key="member.name">
                  <div class="flex flex-row gap-4 items-center justify-space-between">
                    <div class="flex gap-2 items-center">
                      <div class="border iconClass w-[35px] aspect-square rounded-15xl">
                        <!-- <img :src="member.image" alt="" class="w-full h-full rounded-15xl" /> -->
                      </div>
                      <div class=" responsive-body-small font-bold">{{ member.name }}</div>
                    </div>
                    <div v-if="member.isLeader"
                         class="bg-black text-white responsive-body-xsmall rounded-15xl px-2 h-4.5">
                      {{ t('tripOverview.leader') }}
                    </div>
                  </div>
                </div>
              </div>
            </ElCollapseItem>
          </ElCollapse>
        </div>


        <div v-if="tripState === 'payment'" class="budget-card bg-primary-blue grow rounded-11xl p-7 w-2/4">
          <ElCollapse model-value="savedOffers" id="trip-budget">
            <ElCollapseItem name="savedOffers">
              <template #title>
                <div class="flex w-full justify-between">
                  <div class="text-white">{{ t('tripOverview.tripBudget') }}</div>
                  <div v-if="offers.length !== 0" class="underline cursor-pointer text-white"
                       @click="event => { event.stopPropagation(); $emit('go-to-budget') }">
                    <div class="header-button">{{ t('tripOverview.viewDetails') }}</div>
                  </div>
                </div>
              </template>
              <apexchart width="100%" type="radialBar" :options="radialBarOptions" :series="radialBarSeries">
              </apexchart>
            </ElCollapseItem>
          </ElCollapse>
        </div>

        <div v-if="tripState === 'offer'" class="booking-card bg-background-light rounded-11xl p-7 w-2/4">
          <ElCollapse model-value="savedOffers" class="h-full" id="booking-summary">
            <ElCollapseItem class="h-full" name="savedOffers">
              <template #title>
                <div class="flex w-full h-full justify-between">
                  <div>{{ t('tripOverview.bookingSummary') }}</div>
                </div>
              </template>

              <div class="flex align-center items-center justify-center font-semibold text-center w-6/12 lg:w-[15vw]">
                {{ t('tripOverview.noBookingYet') }}
              </div>
            </ElCollapseItem>
          </ElCollapse>
        </div>

      </div>
      <div v-if="(tripState === 'offer' && offers) || tripState === 'payment'"
           class="overview-card bg-gradient-to-r from-gradients-ghostWhite to-gradients-aliceBlue to-40% p-7 rounded-11xl">
        <ElCollapse model-value="savedOffers" id="suggested-itinerary">
          <ElCollapseItem name="savedOffers">
            <template #title>
              <div class="flex w-full justify-between">
                <div>{{ t('tripOverview.suggestedItinerary') }}</div>
                <div v-if="offers.length !== 0" class="header-button underline cursor-pointer"
                     @click="event => { event.stopPropagation(); $emit('go-to-itinerary') }">
                  {{ t('tripOverview.seeAll') }}
                </div>
              </div>
            </template>
            <div>
              <div class="flex flex-row overflow-x-scroll gap-8">
                <div v-for="route in routes" :key="route.title">
                  <div :style="{ backgroundImage: `url('${route.img}')` }"
                       class="routes-img bg-cover rounded-11xl flex items-start w-[10vw] h-56">
                    <div class="p-5 responsive-title-5 text-white flex items-center">
                      {{ route.title }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ElCollapseItem>
        </ElCollapse>
      </div>

    </div>
  </div>
</template>

<style scoped>
.el-collapse {
  @apply border-t-0 border-r-0 border-b-0 flex-row-reverse;
}

:deep(.el-collapse-item__wrap) {
  @apply border-b-0 border-0 mt-8 bg-[unset];
}

:deep(#crew .el-collapse-item__wrap) {
  @apply mt-2;
}

:deep(#trip-budget .el-collapse-item__wrap) {
  @apply mt-0;
}


:deep(.el-collapse-item__header) {
  @apply border-0 flex-row-reverse text-2xl font-bold justify-start;
}

:deep(.v-input) {
  @apply text-2xl;
}

:deep(.el-collapse-item__content) {
  padding-bottom: 0;
  font-size: 1rem;
}

:deep(#saved-offers .el-collapse-item__content) {
  @apply bg-primary-ciel w-full;
}

:deep(#trip-budget .el-collapse-item__content) {
  @apply bg-primary-blue;
}

:deep(#flight-options .el-collapse-item__content) {
  @apply bg-primary-green;
}

:deep(#arrival-time .el-collapse-item__content) {
  @apply bg-primary-orange;
}

:deep(#checklist .el-collapse-item__content) {
  @apply bg-primary-yellow;
}

:deep(#suggested-itinerary .el-collapse-item__content) {
  @apply bg-gradient-to-r from-gradients-ghostWhite to-gradients-aliceBlue to-40%;
}

:deep(#plan .el-collapse-item__content) {
  @apply bg-background-light;
}

:deep(#booking-summary .el-collapse-item__content) {
  display: flex;
  justify-content: center;
  height: 100%;
}

:deep(#booking-summary .el-collapse-item__wrap) {
  margin-top: 0;
  height: calc(100% - 76px);
}

:deep(#saved-offers .el-collapse-item__wrap) {
  display: flex;
  justify-content: center;
  margin-top: 0;
}

:deep(#saved-offers .el-collapse-item__wrap:has(.offers-list)) {
  justify-content: space-between;
}

:deep(#saved-offers .el-collapse-item__content) {
  display: flex;
  flex-direction: column;
  gap: .5rem;
  align-items: center;
}

.header-button:hover {
  opacity: .6;
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}

.placeholderImage {
  width: 150px;
  height: 150px;
}

.iconClass {
  height: 35px;
  width: 35px;
  border: 2px solid #85D7AB !important;
}

.arrow__btn:hover .arrow__btn_icon {
  filter: brightness(0) invert(1);
}

/* Step bar */
#crew {
  --body-background-color: #e5f4ff;
  --v-progress-left: 50px;
  --v-progress-item-height-width: 12px;
  --v-progress-line-height: 3rem;
  --v-progress-line-width: 3px;
  --v-progress-gap: 13px;
  --v-progress-last-height: 5rem;
  --v-progress-inprogress-height: 153px;
  --blue: #0081C9;
  --green: #159895;
  --light-blue: #5BC0F8;
  --v-progress-border: 8px;
  --v-progress-outline: 1px;
  --v-last-completed-height: calc((100% + var(--v-progress-inprogress-height)) / 2);
}

.v-progress {
  padding: 10px 0;
  margin-left: calc(var(--v-progress-left) + var(--v-progress-border) - var(--v-progress-outline) / 2);
  font-size: 1rem;
  font-weight: 600;
}

.v-progress button {
  align-items: center;
  background: #000;
  color: #fff;
  font-size: .875rem;
  border-radius: 1.875rem !important;
  padding: 7px 20px;
}

.v-progress-item {
  position: relative;
  height: var(--v-progress-line-height) !important;
}

.v-progress-item.inprogress .content.request {
  background: unset;
  height: fit-content !important;
  padding: 5px 20px;
  font-size: 1rem;
}

.v-progress-item.completed .request .waiting {
  display: none;
}

.v-progress-item.completed .request .approved {
  display: block;
}

.v-progress-item:not(.inprogress) {
  padding-left: 20px;
  padding-right: 20px;
}

.v-progress-item .payment {
  display: flex;
  line-height: 1.75rem;
}

.v-progress-item .payment-date {
  display: flex;
  gap: 2px;
  font-weight: 400;
  font-size: .875rem;
}

.v-progress-item:before {
  content: "";
  display: inline-block;
  position: absolute;
  width: var(--v-progress-item-height-width);
  height: var(--v-progress-item-height-width);
  left: calc(0px - var(--v-progress-left));
  border-radius: 50%;
  background-color: #000;
  z-index: 10;
}

.v-progress-item:last-child:after {
  border: 0 !important;
}

.v-progress-item:not(.completed)::before {
  background-color: #f8ff99;
  outline: var(--v-progress-outline) solid #000;
  border: 4px solid #f8ff99;
  height: calc(var(--v-progress-border) + var(--v-progress-item-height-width));
  width: calc(var(--v-progress-border) + var(--v-progress-item-height-width));
  left: calc(0px - var(--v-progress-left) - var(--v-progress-border) / 2);
  top: 0;
}

.v-progress-item:after {
  content: "";
  display: inline-block;
  position: absolute;
  height: 100%;
  top: calc(var(--v-progress-item-height-width));
  left: calc(0px - var(--v-progress-left) + var(--v-progress-item-height-width) / 2 - var(--v-progress-line-width) / 2);
  border-left: var(--v-progress-line-width) solid #B7B6BE;

}

.v-progress-item.completed:after {
  border-color: #000;
}

.v-progress-item.completed:before {
  content: url('@/assets/icons/svg/step-bar-checkmark.svg');
  font-size: 11px;
  text-align: center;
  color: white;
  background: #000;
  height: calc(var(--v-progress-border) + var(--v-progress-item-height-width));
  width: calc(var(--v-progress-border) + var(--v-progress-item-height-width));
  line-height: calc(var(--v-progress-border) + var(--v-progress-item-height-width));
  left: calc(0px - var(--v-progress-left) - var(--v-progress-border) / 2);
}

.v-progress-item.completed.last-completed:after {
  height: var(--v-last-completed-height);
}

.v-progress-item.inprogress:not(:first-child) {
  height: fit-content !important;
  padding-bottom: calc(var(--v-progress-line-height) / 2) !important;
}

.v-progress-item.inprogress .content {
  display: flex;
  flex-direction: column;
  background: #FFFFFF66;
  height: fit-content !important;
  padding: 25px 20px;
  font-size: 1.625rem;
  row-gap: .5rem;
}

.v-progress-item.inprogress .content .payment {
  line-height: 1.25;
}

.v-progress .receipt {
  font-size: .875rem;
}

.v-progress-item.inprogress:before {
  background-color: #000;
  outline: var(--v-progress-outline) solid #000;
  border: 4px solid #f8ff99;
  height: calc(var(--v-progress-border) + var(--v-progress-item-height-width));
  width: calc(var(--v-progress-border) + var(--v-progress-item-height-width));
  left: calc(0px - var(--v-progress-left) - var(--v-progress-border) / 2);
  top: calc(50% - var(--v-progress-item-height-width) / 2);
  transform: translateY(calc(-50% - var(--v-progress-line-width)));
}

.v-progress-item.inprogress:after {
  border: 1px dashed #000;
  padding-bottom: 40px;
}

.v-progress-item.inprogress .payment-date {
  position: absolute;
  right: 25px;
  display: flex;
  align-items: center;
  gap: 2px;
}

.v-progress-item.inprogress .down-price {
  display: block;
}

.v-progress-item.inprogress .pay-content {
  flex-direction: column;
  line-height: 1.2;
}

.v-progress-item.completed .payment-date {
  display: flex;
  gap: 2px;
}

.v-progress-item.inprogress .rest-payment, .v-progress-item.inprogress button {
  display: flex;
}


.v-progress-item.inprogress .payment-date:before {
  content: url("@/assets/icons/svg/Alert_cycle_16x16.svg");
  display: inline-block;
  width: 15px;
  height: 15px;
  left: -20px;
  position: absolute;
}

.v-progress-item.inprogress .until {
  display: block;
}

.v-progress-item.completed .until {
  display: none;
}

.v-progress-item.completed .paid {
  display: inline-block;
}

.v-progress-item.inprogress .rest-price {
  display: block;
}

.v-progress-item.completed .receipt {
  display: flex;
}

.arrival-time.v-progress-item.completed.last-completed::after {
  height: inherit !important
}

:deep(#flight-from__field) {
  font-size: 1.5rem;
}

.overview-card:has(img[src$="no-offer-img.svg"]) {
  height: 100%
}

/*  Step bar end */

@media (min-width: 1551px) {
  .header-button {
    font-size: 1rem;
  }
}

@media (max-width: 1550px) {
  .header-button {
    font-size: .875rem;
  }

  :deep(.el-collapse-item__header) {
    font-size: 1rem;
  }

  .days-count {
    font-size: 2.5rem;
  }

  .days-trip__text {
    font-size: 1rem;
  }

  .overview-card__location {
    font-size: .875rem;
  }

  .booksummary-subtitle__text,
  .arrival-time-subtitle__text,
  .booksummary-status__button,
  .flight-subtitle__text {
    font-size: .75rem;
  }

  .booksummary-content__text,
  .arrival-time-flight__content,
  .icon-box__title {
    font-size: 1.125rem;
  }

  .booksummary-manager-content__text,
  .icon-box__subtitle {
    font-size: 0.875rem;
  }

  .booksummary-content-price__text {
    font-size: 1.25rem;
  }

  :deep(#flight-from__field) {
    font-size: 1.25rem;
  }

  .v-progress, .v-progress-item.inprogress .content.request {
    font-size: 1rem;
  }

  .v-progress-item .payment-date {
    font-size: .75rem;
  }

  .v-progress-item.inprogress .content {
    font-size: 1.5rem;
  }

  .req-insurance__button-inactive,
  .req-insurance__button,
  .provision-btn {
    font-size: .875rem;
  }

  .routes-img {
    width: 14vw;
    height: 220px;
  }
}

@media (max-width: 1250px) {
  .routes-img {
    width: 16vw;
    height: 220px;
  }
}

@media (max-width: 1165px) {
  .overview-content {
    flex-direction: column !important;
  }

  .overview-content-left {
    width: 100%;
  }

  .overview-content-right {
    width: 100%;
  }

  .overview-card {
    padding: 1rem;
  }

  .routes-img {
    width: 16vw;
    height: 220px;
  }
}

@media (max-width: 1024px) {
  .overview-card__location {
    font-size: .75rem;
  }

  .routes-img {
    width: 20vw;
    height: 220px;
  }
}

@media (max-width: 850px) {
  .routes-img {
    width: 22vw;
    height: 220px;
  }
}

@media (max-width: 735px) {
  .routes-img {
    width: 26vw;
    height: 200px;
  }
}

@media (max-width: 685px) {
  #crew {
    --v-progress-line-height: 4.5rem;
  }

  :deep(.el-collapse-item__header) {
    font-size: 0.875rem;
  }

  :deep(.el-collapse-item__wrap) {
    @apply mt-4;
  }

  .header-button {
    font-size: .75rem;
  }

  .v-progress-item.inprogress .content.request {
    font-size: 1rem;
  }

  .booksummary-content__text {
    font-size: 1rem;
  }

  .booksummary-subtitle__text {
    font-size: .625rem;
  }

  .booksummary-content-price__text {
    font-size: 1.125rem;
  }

  .booksummary-status__button {
    font-size: .625rem;
  }

  .arrow__btn {
    padding-left: .875rem !important;
    padding-right: .875rem !important;
  }

  :deep(#flight-from__field) {
    font-size: 1.25rem;
  }

  .icon-box__title {
    font-size: 1rem;
  }

  .req-insurance__button-inactive,
  .req-insurance__button,
  .provision-btn {
    font-size: .75rem;
  }

  .v-progress-item.inprogress .content {
    font-size: 1.25rem;
  }

  .v-progress-item.completed .v-progress-content {
    flex-direction: column;
  }

  .v-progress-item.completed .receipt {
    margin-left: 0 !important;
  }

  .v-progress-item.inprogress .pay-content .rest-payment,
  .v-progress-item.inprogress .pay-content .payment-date {
    margin-top: .5rem;
  }

  .v-progress-item.inprogress .payment-date {
    position: unset;
    margin-left: 20px;
  }

  .v-progress-item.inprogress .payment-date::before {
    left: 20px;
  }

  .v-progress-item .v-progress-content {
    line-height: 1.5;
  }

  .double-col {
    flex-direction: column;
    row-gap: 1rem;
  }

  .double-col .crew-card,
  .double-col .booking-card {
    width: 100%;
  }

  .double-col .budget-card {
    width: 100%;
  }

  .routes-img {
    width: 30vw;
    height: 200px;
  }
}

@media (max-width: 639px) {
  .provision-plan-item {
    flex-direction: column;
    align-items: unset !important;
  }

  .provision-plan-item:not(:last-child) {
    margin-bottom: 1rem;
  }
}

@media (max-width: 565px) {
  .icon-box {
    flex-direction: column !important;
    align-items: unset;
  }

  .icon-box button:not(.chev-btn):not(.provision-btn) {
    margin-left: 4.125rem;
  }

  .days-trip__text {
    font-size: 0.875rem;
  }

  .days-count_main {
    flex-direction: column;
  }

  .days-count {
    font-size: 1.875rem;
  }
}

@media (max-width: 485px) {
  .routes-img {
    width: 40vw;
    height: 200px;
  }
}

@media (max-width: 400px) {
  .routes-img {
    width: 50vw;
    height: 200px;
  }
}
</style>